import { type Query, type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ResponseType, TextAtoms } from '@/types';

/**
 * Simple utility functions to reduce code duplication in optimistic updates
 * These are focused, small helpers that solve specific common patterns
 */

// ============================================================================
// Optimistic Mutation Factory
// ============================================================================

/** Base configuration shared by all mutation types */
interface BaseMutationConfig<TVariables> {
    /** Function to get query keys to invalidate on settle */
    getQueryKeys: (variables: TVariables) => readonly (readonly unknown[])[];
    /** Get the primary query key for optimistic updates */
    getPrimaryQueryKey: (variables: TVariables) => readonly unknown[];
    /** Additional query keys to cancel and snapshot (for cascading updates) */
    getAdditionalQueryKeys?: (variables: TVariables) => Array<{
        queryKey: readonly unknown[];
        rollbackKey: string;
    }>;
    /** Custom update for additional caches (e.g., updating related entity counts) */
    updateAdditionalCaches?: (queryClient: QueryClient, variables: TVariables, optimisticEntity?: { id: number }) => void;
}

/** Configuration for CREATE mutations - requires createOptimisticEntity */
interface CreateMutationConfig<TData, TVariables, TEntity extends { id: number }> extends BaseMutationConfig<TVariables> {
    mutationFn: (variables: TVariables) => Promise<ResponseType<TData>>;
    mutationType: 'create';
    /** Required: Create the optimistic entity with a temporary negative ID */
    createOptimisticEntity: (variables: TVariables) => TEntity;
}

/** Configuration for UPDATE mutations - requires getEntityId and updateEntity */
interface UpdateMutationConfig<TData, TVariables, TEntity extends { id: number }> extends BaseMutationConfig<TVariables> {
    mutationFn: (variables: TVariables) => Promise<ResponseType<TData>>;
    mutationType: 'update';
    /** Required: Get the entity ID from variables */
    getEntityId: (variables: TVariables) => number;
    /** Required: Update the entity optimistically */
    updateEntity: (entity: TEntity, variables: TVariables) => TEntity;
}

/** Configuration for DELETE mutations - requires getEntityId */
interface DeleteMutationConfig<TData, TVariables, _TEntity extends { id: number }> extends BaseMutationConfig<TVariables> {
    mutationFn: (variables: TVariables) => Promise<ResponseType<TData>>;
    mutationType: 'delete';
    /** Required: Get the entity ID from variables */
    getEntityId: (variables: TVariables) => number;
}

/** Union type of all mutation configs - enforces correct fields per mutation type */
type OptimisticMutationConfig<TData, TVariables, TEntity extends { id: number }> =
    | CreateMutationConfig<TData, TVariables, TEntity>
    | UpdateMutationConfig<TData, TVariables, TEntity>
    | DeleteMutationConfig<TData, TVariables, TEntity>;

/** Context stored during optimistic update for rollback */
interface MutationContext<TEntity> {
    previousData: TEntity[] | undefined;
    additionalSnapshots: Record<string, unknown>;
    optimisticId?: number;
}

/**
 * Factory hook for creating optimistic mutations with consistent behavior
 * Handles: cancel queries, snapshot, optimistic update, rollback on error, replace on success, invalidate on settled
 *
 * @example
 * // Create mutation
 * useOptimisticMutation<IFountainAct, CreateActVars, IFountainAct>({
 *   mutationType: 'create',
 *   mutationFn: ({ projectId, data }) => api.createAct(projectId, data),
 *   getPrimaryQueryKey: ({ projectId }) => actsEntity.fetchers.list.key({ projectId }),
 *   getQueryKeys: ({ projectId }) => [actsEntity.fetchers.list.key({ projectId })],
 *   createOptimisticEntity: ({ projectId, data }) => ({ id: -Date.now(), ... }),
 * });
 *
 * @example
 * // Update mutation
 * useOptimisticMutation<IFountainAct, UpdateActVars, IFountainAct>({
 *   mutationType: 'update',
 *   mutationFn: ({ projectId, actId, data }) => api.updateAct(projectId, actId, data),
 *   getPrimaryQueryKey: ({ projectId }) => actsEntity.fetchers.list.key({ projectId }),
 *   getQueryKeys: ({ projectId }) => [actsEntity.fetchers.list.key({ projectId })],
 *   getEntityId: ({ actId }) => actId,
 *   updateEntity: (entity, { data }) => ({ ...entity, ...data }),
 * });
 *
 * @example
 * // Delete mutation
 * useOptimisticMutation<void, DeleteActVars, IFountainAct>({
 *   mutationType: 'delete',
 *   mutationFn: ({ projectId, actId }) => api.deleteAct(projectId, actId),
 *   getPrimaryQueryKey: ({ projectId }) => actsEntity.fetchers.list.key({ projectId }),
 *   getQueryKeys: ({ projectId }) => [actsEntity.fetchers.list.key({ projectId })],
 *   getEntityId: ({ actId }) => actId,
 * });
 */
export function useOptimisticMutation<TData, TVariables, TEntity extends { id: number }>(
    config: OptimisticMutationConfig<TData, TVariables, TEntity>,
) {
    const queryClient = useQueryClient();

    return useMutation<ResponseType<TData>, Error, TVariables, MutationContext<TEntity>>({
        mutationFn: config.mutationFn,

        onMutate: async (variables: TVariables) => {
            const primaryQueryKey = config.getPrimaryQueryKey(variables);

            // Cancel outgoing refetches for primary query
            await queryClient.cancelQueries({ queryKey: primaryQueryKey });

            // Snapshot the previous value
            const previousData = queryClient.getQueryData<TEntity[]>(primaryQueryKey);

            // Handle additional query keys (cancel and snapshot)
            const additionalSnapshots: Record<string, unknown> = {};
            if (config.getAdditionalQueryKeys) {
                const additionalKeys = config.getAdditionalQueryKeys(variables);
                for (const { queryKey, rollbackKey } of additionalKeys) {
                    await queryClient.cancelQueries({ queryKey });
                    additionalSnapshots[rollbackKey] = queryClient.getQueryData(queryKey);
                }
            }

            let optimisticId: number | undefined;

            // Apply optimistic update based on mutation type
            if (config.mutationType === 'create') {
                const optimisticEntity = config.createOptimisticEntity(variables);
                optimisticId = optimisticEntity.id;
                queryClient.setQueryData<TEntity[]>(primaryQueryKey, old => {
                    if (!old) return [optimisticEntity];
                    // Insert sorted by order when entity has one, otherwise append
                    const entityOrder = 'order' in optimisticEntity ? (optimisticEntity.order as number) : undefined;
                    if (typeof entityOrder === 'number') {
                        const idx = old.findIndex(item => (('order' in item ? (item.order as number) : null) ?? Infinity) >= entityOrder);
                        if (idx === -1) return [...old, optimisticEntity];
                        return [...old.slice(0, idx), optimisticEntity, ...old.slice(idx)];
                    }
                    return [...old, optimisticEntity];
                });
            } else if (config.mutationType === 'update') {
                const entityId = config.getEntityId(variables);
                queryClient.setQueryData<TEntity[]>(primaryQueryKey, old => {
                    if (!old) return old;
                    return old.map(entity => (entity.id === entityId ? config.updateEntity(entity, variables) : entity));
                });
            } else if (config.mutationType === 'delete') {
                const entityId = config.getEntityId(variables);
                queryClient.setQueryData<TEntity[]>(primaryQueryKey, old => {
                    if (!old) return old;
                    return old.filter(entity => entity.id !== entityId);
                });
            }

            // Apply additional cache updates
            if (config.updateAdditionalCaches) {
                const optimisticEntity = config.mutationType === 'create' ? config.createOptimisticEntity(variables) : undefined;
                config.updateAdditionalCaches(queryClient, variables, optimisticEntity);
            }

            return { previousData, additionalSnapshots, optimisticId };
        },

        onError: (_err, variables, context) => {
            if (!context) return;

            const primaryQueryKey = config.getPrimaryQueryKey(variables);

            // Rollback primary query
            if (context.previousData !== undefined) {
                queryClient.setQueryData(primaryQueryKey, context.previousData);
            }

            // Rollback additional queries
            if (config.getAdditionalQueryKeys) {
                const additionalKeys = config.getAdditionalQueryKeys(variables);
                for (const { queryKey, rollbackKey } of additionalKeys) {
                    const snapshotData = context.additionalSnapshots[rollbackKey];
                    if (snapshotData !== undefined) {
                        queryClient.setQueryData(queryKey, snapshotData);
                    }
                }
            }
        },

        onSuccess: (result, variables, context) => {
            if (!result.success || !result.data || !context) return;

            // For create mutations, replace optimistic entity with real data
            if (config.mutationType === 'create' && context.optimisticId) {
                const realData = result.data as unknown as TEntity;
                const primaryQueryKey = config.getPrimaryQueryKey(variables);
                queryClient.setQueryData<TEntity[]>(primaryQueryKey, old => {
                    if (!old) return old;
                    return old.map(entity => (entity.id === context.optimisticId ? realData : entity));
                });
            }
        },

        onSettled: (_result, _error, variables) => {
            const queryKeys = config.getQueryKeys(variables);
            for (const queryKey of queryKeys) {
                queryClient.invalidateQueries({ queryKey });
            }
        },
    });
}

// Helper to calculate order between two atoms
export const calculateOrderBetween = (previousAtom: TextAtoms | null, nextAtom: TextAtoms | null): number => {
    if (!previousAtom && !nextAtom) {
        return 1000; // First atom
    }
    if (!previousAtom && nextAtom) {
        return Math.max(1000, nextAtom.order / 2); // Insert at beginning
    }
    if (previousAtom && !nextAtom) {
        return previousAtom.order + 1000; // Insert at end
    }
    if (previousAtom && nextAtom) {
        const midpoint = (previousAtom.order + nextAtom.order) / 2;
        // Ensure we have enough space (minimum gap of 1)
        return Math.max(previousAtom.order + 1, Math.min(nextAtom.order - 1, midpoint));
    }
    return 1000; // Fallback
};

// Helper to cancel scene-related queries
export const cancelSceneQueries = async (queryClient: QueryClient) => {
    return queryClient.cancelQueries({
        predicate: query => {
            const key = query.queryKey;
            return Array.isArray(key) && key[0] === 'scenes';
        },
    });
};

// Helper to find scene text atoms queries
export const findSceneTextAtomQueries = (queryClient: QueryClient): Query[] => {
    return queryClient
        .getQueryCache()
        .getAll()
        .filter(query => {
            const queryKey = query.queryKey;
            return Array.isArray(queryKey) && queryKey[0] === 'scenes' && queryKey[1] === 'textAtoms';
        });
};

// Helper to find character queries by project ID
export const findCharacterQueries = (queryClient: QueryClient, projectId: number) => {
    return queryClient.getQueriesData({
        predicate: query => {
            const key = query.queryKey;
            return (
                Array.isArray(key) &&
                key[0] === 'characters' &&
                key[1] === 'list' &&
                key[2] &&
                typeof key[2] === 'object' &&
                'projectId' in key[2] &&
                key[2].projectId === projectId
            );
        },
    });
};

// Helper to rollback multiple queries
export const rollbackQueries = (queryClient: QueryClient, rollbackData: Array<{ queryKey: readonly unknown[]; data: unknown }>) => {
    rollbackData.forEach(({ queryKey, data }) => {
        queryClient.setQueryData(queryKey, data);
    });
};

// Query-key shape matchers for the two caches that carry scene tags. Shared by
// the find* getters and the cancel helper so a queryKey restructure is a
// one-line change.
const isScenePresentationKey = (key: unknown): boolean => Array.isArray(key) && key[0] === 'scenes' && key[1] === 'presentations';
const isFountainSceneKey = (key: unknown): boolean => Array.isArray(key) && key[0] === 'fountain' && key[1] === 'scenes';

// Helper to find scene presentation queries
export const findScenePresentationQueries = (queryClient: QueryClient) => {
    return queryClient.getQueriesData({ predicate: query => isScenePresentationKey(query.queryKey) });
};

// Helper to find fountain scene queries (used by Fountain Editor)
export const findFountainSceneQueries = (queryClient: QueryClient) => {
    return queryClient.getQueriesData({ predicate: query => isFountainSceneKey(query.queryKey) });
};

// Cancel in-flight scene-presentation + fountain-scene refetches so a response
// that started before an optimistic tag update can't land afterwards and
// clobber it (the flicker where a just-removed/added chip momentarily reverts).
export const cancelSceneTagQueries = (queryClient: QueryClient) =>
    queryClient.cancelQueries({
        predicate: query => isScenePresentationKey(query.queryKey) || isFountainSceneKey(query.queryKey),
    });
